/**
 * O mapa da área de Configurações — Direção A da proposta de 2026-09-10
 * (`docs/designsystem/propostas/area-de-configuracoes.html`).
 *
 * Esta lista é a ÚNICA fonte de verdade e serve a quatro coisas:
 *   1. o trilho da área (`ConfiguracoesRail`);
 *   2. a gaveta do celular (`MobileNav`);
 *   3. a busca global (Ctrl+K), que lista cada tela daqui;
 *   4. saber se a rota atual é de configuração, para o `MainLayout` trocar a
 *      sidebar pelo trilho.
 * Se as listas divergirem, a tela abre com a navegação errada — por isso uma só.
 *
 * **As rotas NÃO mudaram de endereço de propósito.** Mover `/users` para
 * `/configuracoes/usuarios` obrigaria a caçar todo `navigate()` e `<Link>` do
 * app, mais o onboarding e os links que o cliente já salvou — superfície de
 * regressão grande para ganho cosmético. O que mudou foi a CASCA: as mesmas
 * rotas passam a ser filhas de um layout que troca a sidebar pelo trilho.
 * A exceção é `/configuracoes/conta`, que já nasceu com o prefixo.
 *
 * O mesmo vale para o submenu **Sistema › Relatórios** (2026-10-05): Mensagens e
 * Assistente continuam em `/sistema/mensagens` e `/sistema/assistente`; o que
 * mudou foi onde ficam no menu, não o endereço.
 */

import { temFuncionalidade } from './permissions';

export interface ItemConfig {
    rotulo: string;
    to: string;
    /** Nenhuma = todo mundo vê (o caso da própria conta). */
    permissoes?: string[];
    /** Planos some para as empresas 4 e 8 — regra que já existia na sidebar. */
    escondeParaEmpresa4Ou8?: boolean;
    /** Funcionalidade (Sistema › Funcionalidades) que precisa estar ligada para o item aparecer. */
    funcionalidade?: string;
}

/**
 * Um submenu dentro do grupo: um rótulo sem tela própria com itens embaixo.
 * Nasceu para "Relatórios" em Sistema, que vai crescer um item por relatório
 * (saúde, uso de tela, padrões…) — se cada um entrasse solto no grupo, Sistema
 * viraria uma lista comprida onde cadastro e relatório se misturam.
 */
export interface SubmenuConfig {
    rotulo: string;
    itens: ItemConfig[];
}

export type EntradaConfig = ItemConfig | SubmenuConfig;

export const ehSubmenu = (e: EntradaConfig): e is SubmenuConfig => 'itens' in e;

export interface GrupoConfig {
    titulo: string;
    itens: EntradaConfig[];
}

export const GRUPOS_CONFIG: GrupoConfig[] = [
    {
        titulo: 'Conta',
        itens: [
            { rotulo: 'Meu perfil', to: '/configuracoes/conta' },
        ],
    },
    {
        titulo: 'Empresa',
        itens: [
            { rotulo: 'Minhas empresas', to: '/gerenciar-empresa', permissoes: ['dias_funcionamento'] },
            { rotulo: 'Conexões', to: '/connections', permissoes: ['gestao_conexoes'] },
            /* A porta da primeira conexão das integrações (RD Station, anúncios). Mesma
               permissão de Conexões, que é onde o app conectado passa a morar. */
            { rotulo: 'Apps', to: '/apps', permissoes: ['gestao_conexoes'] },
        ],
    },
    {
        titulo: 'Pessoas',
        itens: [
            { rotulo: 'Usuários', to: '/users', permissoes: ['gestao_usuarios'] },
            { rotulo: 'Perfis de acesso', to: '/tipos-usuario', permissoes: ['gestao_tipos_usuario'] },
            { rotulo: 'Equipes', to: '/equipes', permissoes: ['gestao_equipes'] },
        ],
    },
    {
        titulo: 'Vendas',
        itens: [
            /* O funil vem antes de tudo no grupo: ele é o que desenha as colunas do quadro. */
            { rotulo: 'Funil', to: '/funil', permissoes: ['gestao_funil'] },
            { rotulo: 'Metas', to: '/metas', permissoes: ['gestao_metas'] },
            {
                rotulo: 'Campos personalizados', to: '/campos-personalizados',
                // As duas grafias existem no catálogo — mantido como no mapa de rotas.
                permissoes: ['gestao_campos_personalizados'],
            },
            /* Atrás da funcionalidade: empresa que não pediu não ganha item de menu novo.
               A regra de "quem inicia a conversa" é outra flag (conversa_ativa_fora_do_funil). */
            {
                rotulo: 'Tipos de contato', to: '/tipos-contato',
                permissoes: ['gestao_tipos_contato'], funcionalidade: 'tipo_contato',
            },
            { rotulo: 'Motivos de perda', to: '/motivos-perda', permissoes: ['gestao_motivo_perda'] },
            { rotulo: 'Tarefas padrão', to: '/tarefas-padrao', permissoes: ['dias_funcionamento'] },
            { rotulo: 'Tipos de tarefa', to: '/tipos-tarefa', permissoes: ['dias_funcionamento'] },
            /* Proposta, orçamento e contrato em PDF, emitidos na oportunidade. Só com a
               funcionalidade ligada: desligada, nem o item nem a rota existem. */
            { rotulo: 'Modelos de documento', to: '/modelos', permissoes: ['dias_funcionamento'], funcionalidade: 'modelos' },
        ],
    },
    {
        /* Era "Chatbot": o atalho é do vendedor, não do robô, e o grupo já misturava os dois. */
        titulo: 'Atendimento',
        itens: [
            { rotulo: 'Atalhos de mensagem', to: '/atalhos', permissoes: ['atalhos'] },
            { rotulo: 'Regras do chat', to: '/chat-rules', permissoes: ['regras_chatbot'] },
            { rotulo: 'Produtos e serviços', to: '/products', permissoes: ['gestao_produtos'] },
        ],
    },
    {
        titulo: 'Sistema',
        itens: [
            { rotulo: 'Empresas', to: '/empresas', permissoes: ['gestao_empresas'] },
            /* "Planos" é a GESTÃO de planos, não o plano da empresa logada —
               este último é "Meu plano" (`/plano`), que segue no menu da conta. */
            { rotulo: 'Planos', to: '/gerenciar-planos', permissoes: ['gerenciar_planos'], escondeParaEmpresa4Ou8: true },
            { rotulo: 'Funcionalidades', to: '/sistema/funcionalidades', permissoes: ['gbcode_admin_sistema'] },
            { rotulo: 'Central de ajuda', to: '/admin/help', permissoes: ['gbcode_admin_sistema'] },
            /* O que os usuários mandam pelo botão "Suporte e sugestões" da sidebar. */
            { rotulo: 'Sugestões', to: '/sistema/sugestoes', permissoes: ['gbcode_admin_sistema'] },
            /* Os relatórios da PLATAFORMA (só GB Code) — não confundir com
               `/relatorios`, que responde sobre o negócio do cliente. A régua está
               em `docs/designsystem/propostas/admin/planejamento.html`, §Onde isso
               aparece no menu. Relatório novo entra aqui, não solto no grupo. */
            {
                rotulo: 'Relatórios',
                itens: [
                    /* A primeira: a foto da plataforma. Mensagens e Assistente são o detalhe dela. */
                    { rotulo: 'Visão geral', to: '/sistema/visao-geral', permissoes: ['gbcode_admin_sistema'] },
                    { rotulo: 'Mensagens', to: '/sistema/mensagens', permissoes: ['gbcode_admin_sistema'] },
                    { rotulo: 'Assistente', to: '/sistema/assistente', permissoes: ['gbcode_admin_sistema'] },
                    /* Os leads do brikcrm.com.br (fase 7, etapa A). */
                    { rotulo: 'Site', to: '/sistema/site', permissoes: ['gbcode_admin_sistema'] },
                ],
            },
        ],
    },
];

/** Todos os itens com rota de um grupo, com os de submenu já abertos. */
export const itensDoGrupo = (g: GrupoConfig): ItemConfig[] =>
    g.itens.flatMap(e => (ehSubmenu(e) ? e.itens : [e]));

/**
 * Os grupos que esta pessoa alcança, já sem os itens que ela não vê e sem os
 * grupos que ficaram vazios — um cabeçalho "Sistema" sozinho só avisa que
 * existe algo fora do alcance. A mesma regra vale um nível abaixo: submenu sem
 * nenhum item visível some com o rótulo.
 *
 * Mora aqui, e não no trilho, porque o drawer do celular desenha a mesma
 * lista: enquanto a regra viveu só dentro do `ConfiguracoesRail`, quem entrava
 * pelo telefone não tinha nenhuma tela de Configurações.
 */
export const gruposConfigVisiveis = (
    permissoes: string[],
    empresa4Ou8: boolean,
    temPermissao: (p: string[], r: string[]) => boolean,
): GrupoConfig[] => {
    const ve = (i: ItemConfig) => {
        if (i.escondeParaEmpresa4Ou8 && empresa4Ou8) return false;
        if (i.funcionalidade && !temFuncionalidade(i.funcionalidade)) return false;
        if (!i.permissoes) return true;
        return temPermissao(permissoes, i.permissoes);
    };
    return GRUPOS_CONFIG
        .map(g => ({
            ...g,
            itens: g.itens
                .map(e => (ehSubmenu(e) ? { ...e, itens: e.itens.filter(ve) } : e))
                .filter(e => (ehSubmenu(e) ? e.itens.length > 0 : ve(e))),
        }))
        .filter(g => g.itens.length > 0);
};

/**
 * Casa a rota com o item, incluindo as filhas: `/chat-rules/12` é a tela de
 * Regras do chat, e `/admin/help/new` é a Central de ajuda.
 */
export const rotaCasaCom = (pathname: string, to: string) =>
    pathname === to || pathname.startsWith(to + '/');

/** A rota atual pertence à área de Configurações? */
export const ehRotaDeConfiguracao = (pathname: string) =>
    GRUPOS_CONFIG.some(g => itensDoGrupo(g).some(i => rotaCasaCom(pathname, i.to)));
